import { TOPIC_NODE_UNPLACED_DISPLAY, parkedSubtopicId, isParkedSubtopicId } from "@okf/ootils";

export type DocTopicBar = {
  value: string; // sub-topic nodeId, or the topic's parked pseudo id
  display: string;
  count: number;
  // annoKeys of this document's observations under the bar — the published
  // page filters its annotation cards by these on a bar click (a card's id
  // IS the annotation's annoKey).
  annoKeys: string[];
};

export type DocTopicGroup = {
  subThemeId: string; // topic nodeId — named to match the sub-theme group contract the card renders
  subThemeDisplay: string;
  isTopicGroup: true;
  total: number;
  tags: DocTopicBar[];
};

/**
 * One document's slice of the topic-clustering tree, in the same group
 * shape the Distribution card renders its sub-themes with: one group per
 * topic this document has observations in, its sub-topics as the bars,
 * counted by this document's observations only.
 *
 * The tree endpoint resolves every node's observations with their
 * documentId, so this is a pure walk — no per-document query. A topic's
 * parked observations (in the topic, in no sub-topic) become its
 * "Unplaced" bar, keyed by the parked pseudo id, as everywhere else.
 * Observations the tree placed nowhere at all are not shown: they belong
 * to no topic, so there is no dropdown entry to file them under.
 */
export const groupDocTopicsFromTree = ({ treeData, documentId }: { treeData: any; documentId?: string }): DocTopicGroup[] => {
  if (!treeData?.tree?.length || !documentId) return [];
  const docId = String(documentId);
  const ownObs = (node: any) => (node?.observations || []).filter((o: any) => String(o.documentId) === docId);

  const groups: DocTopicGroup[] = [];
  for (const topic of treeData.tree) {
    const bars: DocTopicBar[] = [];
    const children: any[] = topic.children || [];
    if (children.length) {
      for (const sub of children) {
        const obs = ownObs(sub);
        if (obs.length) {
          bars.push({
            value: String(sub.nodeId),
            display: sub.label,
            count: obs.length,
            annoKeys: obs.map((o: any) => String(o.annoKey)),
          });
        }
      }
    } else {
      // Legacy flat node — the topic is its own row.
      const obs = ownObs(topic);
      if (obs.length) {
        bars.push({ value: String(topic.nodeId), display: topic.label, count: obs.length, annoKeys: obs.map((o: any) => String(o.annoKey)) });
      }
    }
    const parked = (topic.parkedObservations || []).filter((o: any) => String(o.documentId) === docId);
    if (parked.length) {
      bars.push({
        value: parkedSubtopicId(topic.nodeId),
        display: TOPIC_NODE_UNPLACED_DISPLAY,
        count: parked.length,
        annoKeys: parked.map((o: any) => String(o.annoKey)),
      });
    }
    if (!bars.length) continue;
    // Count desc, the parked bar pinned last — the order the explorer and
    // the Annotation Counts widget use.
    const sorted = bars.sort((a, b) => {
      const aP = isParkedSubtopicId(a.value);
      const bP = isParkedSubtopicId(b.value);
      if (aP !== bP) return aP ? 1 : -1;
      return b.count - a.count;
    });
    groups.push({
      subThemeId: String(topic.nodeId),
      subThemeDisplay: topic.label,
      isTopicGroup: true,
      total: sorted.reduce((n, b) => n + b.count, 0),
      tags: sorted,
    });
  }
  return groups.sort((a, b) => b.total - a.total);
};
